import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { flowsheetUrl } from './api';
import { clientApi } from '@/config/axios';
import { useCareViews } from './careViews';
import { renderedRows } from './renderModel';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { activeEvents, eventHeads } from './projection';
import { PrenatalCompactCard } from './PrenatalCompactCard';
import type { ClinicalEvent, ClinicalSubject } from './types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
export interface TimelineEncounter {
  author: string | null;
  author_id: string | null;
  date: string;
  id: string;
  place: string | null;
  status: string;
  type: string;
}
interface Preferences {
  author: string;
  collapsed_groups: Record<string, boolean>;
  date_from: string;
  date_to: string;
  encounter_type: string;
  expanded: Record<string, boolean>;
  group: 'month' | 'phase' | 'none';
  latest: number;
}
const defaults: Preferences = {
  latest: 1,
  author: '',
  date_to: '',
  expanded: {},
  date_from: '',
  group: 'phase',
  collapsed_groups: {},
  encounter_type: 'all',
};
export function encounterPhase(visit: TimelineEncounter, edd?: string | null) {
  const type = visit.type.toLowerCase();
  if (type.includes('postpartum')) return 'Postpartum';
  if (type.includes('newborn')) return 'Newborn';
  if (type.includes('labor') || type.includes('birth')) return 'Intrapartum';
  if (!edd) return 'Dating unavailable';
  const days = 280 + Math.round((Date.parse(visit.date) - Date.parse(edd)) / 86400000);
  return days < 0
    ? 'Before pregnancy dating'
    : days < 98
      ? 'First trimester'
      : days < 196
        ? 'Second trimester'
        : 'Third trimester';
}
export function EpisodeTimeline({
  edd,
  events,
  subjects,
  timeZone,
  episodeId,
  patientId,
}: {
  episodeId: string;
  patientId: string;
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  edd?: string | null;
  timeZone: string;
}) {
  const { user, organization } = useAuth();
  const org = organization?.organization_id;
  const key = `care-timeline:${org}:${user?.id}:${patientId}:${episodeId}`;
  const [prefs, setPrefs] = useState<Preferences>(defaults);
  const [loaded, setLoaded] = useState('');
  const closedGroups = prefs.collapsed_groups;
  const setClosedGroups = (
    next: Record<string, boolean> | ((current: Record<string, boolean>) => Record<string, boolean>),
  ) => setPrefs((p) => ({ ...p, collapsed_groups: typeof next === 'function' ? next(p.collapsed_groups) : next }));
  const [scroll, setScroll] = useState(0);
  const [message, setMessage] = useState('');
  const viewport = useRef<HTMLDivElement>(null);
  const views = useCareViews(`timeline:${patientId}`);
  const query = useQuery({
    enabled: !!org,
    queryKey: ['episode-timeline', org, episodeId],
    queryFn: async () => {
      const data: TimelineEncounter[] = [];
      let offset = 0;
      for (;;) {
        const response = (
          await clientApi.get<{ total: number; data: TimelineEncounter[] }>(
            `${flowsheetUrl(org!)}/episodes/${episodeId}/timeline`,
            { params: { offset, limit: 500 } },
          )
        ).data;
        data.push(...response.data);
        offset += response.data.length;
        if (offset >= response.total) return data;
        if (!response.data.length) throw new Error('Incomplete timeline');
      }
    },
  });
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      setPrefs(saved ? { ...defaults, ...saved } : defaults);
    } catch {
      setPrefs(defaults);
    }
    setLoaded(key);
    setScroll(0);
  }, [key]);
  useEffect(() => {
    if (loaded === key)
      try {
        localStorage.setItem(key, JSON.stringify(prefs));
      } catch {
        /* Local persistence optional. */
      }
  }, [prefs, key, loaded]);
  const all = useMemo(() => query.data || [], [query.data]);
  const filtered = useMemo(
    () =>
      all.filter(
        (e) =>
          (!prefs.date_from || e.date >= prefs.date_from) &&
          (!prefs.date_to || e.date <= prefs.date_to) &&
          (!prefs.author || e.author_id === prefs.author) &&
          (prefs.encounter_type === 'all' || e.type === prefs.encounter_type),
      ),
    [all, prefs],
  );
  const isOpen = (e: TimelineEncounter) =>
    prefs.expanded[e.id] ?? all.slice(0, prefs.latest).some((v) => v.id === e.id);
  const { rows, total } = useMemo(() => {
    const rows: { id: string; top: number; height: number; visit?: TimelineEncounter; group?: string }[] = [];
    let total = 0;
    let lastGroup = '';
    for (const visit of filtered) {
      const group =
        prefs.group === 'phase' ? encounterPhase(visit, edd) : prefs.group === 'month' ? visit.date.slice(0, 7) : '';
      if (group && group !== lastGroup) {
        rows.push({ group, top: total, height: 48, id: `group-${group}-${visit.id}` });
        total += 48;
        lastGroup = group;
      }
      if (!closedGroups[group]) {
        const open = prefs.expanded[visit.id] ?? all.slice(0, prefs.latest).some((v) => v.id === visit.id);
        const height = open ? 460 : 112;
        rows.push({ visit, height, top: total, id: visit.id });
        total += height;
      }
    }
    return { rows, total };
  }, [filtered, prefs, closedGroups, edd, all]);
  const visible = rows.filter((r) => r.top + r.height >= scroll - 500 && r.top <= scroll + 1200);
  const reveal = (id: string) => {
    const visit = all.find((e) => e.id === id);
    if (!visit) return;
    setPrefs((p) => ({
      ...p,
      author: '',
      date_to: '',
      date_from: '',
      encounter_type: 'all',
      expanded: { ...p.expanded, [id]: true },
    }));
    setClosedGroups({});
    window.history.replaceState(null, '', `#flowsheet-encounter-${id}`);
  };
  const pendingReveal = useRef<string | null>(null);
  useEffect(() => {
    const listener = (event: Event) => {
      const id = (event as CustomEvent<string>).detail;
      pendingReveal.current = id;
      reveal(id);
    };
    window.addEventListener('flowsheet-open-encounter', listener);
    return () => window.removeEventListener('flowsheet-open-encounter', listener);
  });
  const initialLink = useRef('');
  useEffect(() => {
    if (loaded !== key || !query.data || initialLink.current === key) return;
    initialLink.current = key;
    const id = window.location.hash.match(/^#flowsheet-encounter-(.+)$/)?.[1];
    if (id) {
      pendingReveal.current = id;
      reveal(id);
    }
  });
  useEffect(() => {
    const id = pendingReveal.current;
    if (!id) return;
    const row = rows.find((r) => r.id === id);
    if (row) {
      viewport.current?.scrollTo({ top: row.top });
      setScroll(row.top);
      pendingReveal.current = null;
    }
  }, [rows]);
  const currentHeads = eventHeads(events);
  const update = (patch: Partial<Preferences>) => setPrefs((p) => ({ ...p, ...patch }));
  const save = async () => {
    try {
      const existing = views.data?.find((v) => v.name === 'Timeline' && v.owner_id === user?.id);
      await views.save({ name: 'Timeline', expected_version: existing?.version || 0, ...prefs });
      setMessage('Timeline preferences synced.');
    } catch {
      setMessage('Preferences were not synced. Local choices are retained; reload saved preferences before retrying.');
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Encounter timeline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => update({ expanded: Object.fromEntries(all.map((v) => [v.id, false])) })}>
            Collapse all
          </Button>
          <Button
            variant="outline"
            onClick={() => update({ expanded: Object.fromEntries(filtered.map((v) => [v.id, true])) })}>
            Expand filtered encounters
          </Button>
          <label>
            Expand latest{' '}
            <input
              min={0}
              max={300}
              type="number"
              value={prefs.latest}
              className="w-20 min-h-11 border"
              aria-label="Expand latest encounters"
              onChange={(e) => update({ expanded: {}, latest: Math.max(0, Math.min(300, Number(e.target.value))) })}
            />
          </label>
          <label>
            Group{' '}
            <select
              value={prefs.group}
              className="min-h-11 border"
              onChange={(e) => update({ group: e.target.value as Preferences['group'] })}>
              <option value="phase">Phase</option>
              <option value="month">Month</option>
              <option value="none">None</option>
            </select>
          </label>
          <label>
            Type{' '}
            <select
              className="min-h-11 border"
              value={prefs.encounter_type}
              onChange={(e) => update({ encounter_type: e.target.value })}>
              <option value="all">All types</option>
              {[...new Set(all.map((v) => v.type))].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          <label>
            Author{' '}
            <select
              value={prefs.author}
              className="min-h-11 border"
              onChange={(e) => update({ author: e.target.value })}>
              <option value="">All authors</option>
              {[...new Map(all.filter((e) => e.author_id).map((e) => [e.author_id, e.author])).entries()].map(
                ([id, name]) => (
                  <option key={id} value={id!}>
                    {name || 'Unknown author'}
                  </option>
                ),
              )}
            </select>
          </label>
          <label>
            From{' '}
            <input
              type="date"
              value={prefs.date_from}
              className="min-h-11 border"
              onChange={(e) => update({ date_from: e.target.value })}
            />
          </label>
          <label>
            To{' '}
            <input
              type="date"
              value={prefs.date_to}
              className="min-h-11 border"
              onChange={(e) => update({ date_to: e.target.value })}
            />
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void save()}>
            Sync timeline preferences
          </Button>
          <Button
            variant="outline"
            disabled={!views.data?.some((v) => v.name === 'Timeline' && v.owner_id === user?.id)}
            onClick={() => {
              const saved = views.data?.find((v) => v.name === 'Timeline' && v.owner_id === user?.id);
              if (saved) setPrefs({ ...defaults, ...saved } as Preferences);
            }}>
            Load synced preferences
          </Button>
        </div>
        {message && <p role="status">{message}</p>}
        {query.isError && (
          <p role="alert">
            Timeline unavailable. <button onClick={() => void query.refetch()}>Retry</button>
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          {filtered.length} encounters. Keyboard: [ collapse, ] expand, j/k move between encounters. Expanded cards
          scroll to show all observations.
        </p>
        <div
          ref={viewport}
          tabIndex={0}
          role="region"
          aria-label="Scrollable encounter timeline"
          className="h-[640px] overflow-auto relative border rounded"
          onScroll={(e) => setScroll(e.currentTarget.scrollTop)}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === '[' || e.key === ']') {
              e.preventDefault();
              update({ expanded: Object.fromEntries(filtered.map((v) => [v.id, e.key === ']'])) });
            }
            if (e.key === 'j' || e.key === 'k') {
              e.preventDefault();
              const positions = rows.filter((r) => r.visit);
              const at = positions.findIndex((r) => r.top >= scroll);
              const next = positions[Math.max(0, Math.min(positions.length - 1, at + (e.key === 'j' ? 1 : -1)))];
              if (next) viewport.current?.scrollTo({ top: next.top });
            }
          }}>
          <div style={{ height: total, position: 'relative' }}>
            {visible.map((row) => (
              <div
                key={row.id}
                style={{ padding: 6, top: row.top, width: '100%', height: row.height, position: 'absolute' }}>
                {row.group ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setClosedGroups((g) => ({ ...g, [row.group!]: !g[row.group!] }))}>
                    {closedGroups[row.group] ? '▸' : '▾'} {row.group}
                  </Button>
                ) : (
                  row.visit &&
                  (() => {
                    const v = row.visit!;
                    const observed = currentHeads.filter((e) => e.encounter_id === v.id);
                    const confirmed = activeEvents(observed);
                    const [summaryHeaders, ...summaryRows] = renderedRows(
                      events.filter((e) => e.encounter_id === v.id),
                      subjects,
                      timeZone,
                      edd || null,
                    );
                    const summary = summaryHeaders
                      ?.map((label, index) =>
                        /GA|BP|Fundal|FHT/.test(label) && summaryRows[0]?.[index] && summaryRows[0][index] !== '—'
                          ? `${label}: ${summaryRows[0][index]}`
                          : '',
                      )
                      .filter(Boolean)
                      .join(' · ');

                    return (
                      <section id={`flowsheet-encounter-${v.id}`} className="h-full border rounded p-3 overflow-auto">
                        <button
                          aria-expanded={isOpen(v)}
                          className="min-h-11 text-left w-full font-medium"
                          onClick={() => update({ expanded: { ...prefs.expanded, [v.id]: !isOpen(v) } })}>
                          {v.date} · {v.type} · {v.place || 'Place not recorded'} · {v.author || 'Author not recorded'}{' '}
                          · {confirmed.filter((e) => e.signed).length}/{confirmed.length} signed
                        </button>
                        {isOpen(v) ? (
                          <>
                            <PrenatalCompactCard
                              edd={edd}
                              events={events}
                              encounterId={v.id}
                              subjects={subjects}
                              timeZone={timeZone}
                            />
                            <a className="underline" href={`/encounters/${v.id}`}>
                              Open full encounter, attachments and tasks
                            </a>
                          </>
                        ) : (
                          <p className="text-sm">
                            {summary ? `${summary} · ` : ''}
                            {confirmed.length} confirmed observations ·{' '}
                            {observed.filter((e) => e.status.startsWith('PENDING')).length} awaiting review
                          </p>
                        )}
                      </section>
                    );
                  })()
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
